import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { Scale, Plus, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";

export default function Journal() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);
  const [reviewing, setReviewing] = useState(false);
  const [f, setF] = useState({
    title: "", context: "", options_considered: "", decision: "", reasoning: "",
    confidence: 60, reversibility: "two_way", stakes: "medium",
  });

  const load = async () => {
    const { data } = await api.get("/journal/entries");
    setEntries(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.title.trim() || !f.context.trim() || !f.decision.trim()) {
      toast.error("Title, context and decision required"); return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/journal/entries", { ...f, confidence: parseInt(f.confidence) });
      toast.success("Decision logged");
      setOpen(false);
      setF({ title: "", context: "", options_considered: "", decision: "", reasoning: "", confidence: 60, reversibility: "two_way", stakes: "medium" });
      setEntries([data, ...entries]);
      setActive(data);
    } catch { toast.error("Failed to save"); }
    finally { setBusy(false); }
  };

  const review = async (id) => {
    setReviewing(true);
    try {
      const { data: r } = await api.post(`/journal/entries/${id}/review`);
      const { data: entry } = await api.get(`/journal/entries/${id}`);
      setActive(entry);
      setEntries(entries.map((e) => e.id === id ? entry : e));
      toast.success("Review complete");
    } catch { toast.error("Review failed"); }
    finally { setReviewing(false); }
  };

  return (
    <div className="space-y-10" data-testid="journal-page">
      <div className="flex items-start justify-between gap-6">
        <div>
          <div className="label-caps mb-2 flex items-center gap-1.5"><Scale className="h-3 w-3 text-emerald-400" />Practice · Decision Journal</div>
          <h1 className="text-3xl sm:text-5xl tracking-tight font-semibold">Log a decision. Learn from it.</h1>
          <p className="text-muted-foreground mt-3 max-w-2xl">
            Capture the context, options and your confidence before the result is known. Review it against the tribunal's judgment framework.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-full" data-testid="journal-new"><Plus className="h-4 w-4 mr-1" />New entry</Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Log a decision</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({...f, title: e.target.value})} placeholder="e.g. Hire first full-time AI engineer vs. keep contractors" data-testid="j-title" className="mt-1.5" /></div>
              <div><Label>Context</Label><Textarea rows={3} value={f.context} onChange={(e) => setF({...f, context: e.target.value})} placeholder="What situation are you deciding in?" data-testid="j-context" className="mt-1.5" /></div>
              <div><Label>Options considered</Label><Textarea rows={2} value={f.options_considered} onChange={(e) => setF({...f, options_considered: e.target.value})} placeholder="A) ... B) ... C) ..." data-testid="j-options" className="mt-1.5" /></div>
              <div><Label>Decision made</Label><Textarea rows={2} value={f.decision} onChange={(e) => setF({...f, decision: e.target.value})} data-testid="j-decision" className="mt-1.5" /></div>
              <div><Label>Reasoning</Label><Textarea rows={2} value={f.reasoning} onChange={(e) => setF({...f, reasoning: e.target.value})} data-testid="j-reasoning" className="mt-1.5" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Confidence (0-100)</Label><Input type="number" min={0} max={100} value={f.confidence} onChange={(e) => setF({...f, confidence: e.target.value})} data-testid="j-confidence" className="mt-1.5" /></div>
                <div><Label>Reversibility</Label><Select value={f.reversibility} onValueChange={(v) => setF({...f, reversibility: v})}><SelectTrigger data-testid="j-reversibility" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="two_way">Two-way (reversible)</SelectItem><SelectItem value="one_way">One-way (irreversible)</SelectItem></SelectContent></Select></div>
                <div><Label>Stakes</Label><Select value={f.stakes} onValueChange={(v) => setF({...f, stakes: v})}><SelectTrigger data-testid="j-stakes" className="mt-1.5"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Low</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="catastrophic">Catastrophic</SelectItem></SelectContent></Select></div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={busy} data-testid="j-submit">{busy ? "Saving…" : "Log decision"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 space-y-2">
          {entries.length === 0 ? (
            <Card className="p-10 text-center text-muted-foreground">
              <Scale className="h-6 w-6 mx-auto mb-2" />
              No entries yet. Log a decision to start.
            </Card>
          ) : entries.map((e) => (
            <button key={e.id} onClick={() => setActive(e)} data-testid={`j-${e.id}`}
              className={`block w-full text-left p-4 rounded-lg border transition-colors duration-200 ${active?.id === e.id ? "border-primary bg-secondary/40" : "border-border hover:border-primary/50"}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-medium truncate">{e.title}</div>
                  <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{e.context}</div>
                </div>
                <div className="shrink-0 flex flex-col items-end gap-1">
                  <Badge variant={e.stakes === "catastrophic" || e.stakes === "high" ? "destructive" : "secondary"} className="text-[9px] font-mono uppercase">{e.stakes}</Badge>
                  <div className="font-mono text-xs text-muted-foreground">{e.confidence}%</div>
                </div>
              </div>
            </button>
          ))}
        </div>

        <div className="lg:col-span-3">
          {active ? (
            <Card className="p-6 md:p-8">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">{new Date(active.created_at).toLocaleString()}</div>
                  <h2 className="text-xl md:text-2xl font-medium tracking-tight mt-1">{active.title}</h2>
                </div>
                <Button onClick={() => review(active.id)} disabled={reviewing} className="rounded-full" data-testid="j-review">
                  <Sparkles className="h-4 w-4 mr-1" />{reviewing ? "Reviewing…" : active.review ? "Re-review" : "AI review"}
                </Button>
              </div>
              <div className="mt-6 grid sm:grid-cols-3 gap-4 text-sm">
                <div><div className="label-caps mb-1">Confidence</div><div className="font-mono text-2xl">{active.confidence}%</div></div>
                <div><div className="label-caps mb-1">Reversibility</div><div className="text-sm">{active.reversibility === "one_way" ? "One-way" : "Two-way"}</div></div>
                <div><div className="label-caps mb-1">Stakes</div><div className="text-sm capitalize">{active.stakes}</div></div>
              </div>
              <div className="mt-6 space-y-4 text-sm">
                <div><div className="label-caps mb-1">Context</div><p className="text-foreground/90 whitespace-pre-wrap">{active.context}</p></div>
                {active.options_considered && <div><div className="label-caps mb-1">Options considered</div><p className="text-foreground/90 whitespace-pre-wrap">{active.options_considered}</p></div>}
                <div><div className="label-caps mb-1">Decision</div><p className="text-foreground/90 whitespace-pre-wrap">{active.decision}</p></div>
                {active.reasoning && <div><div className="label-caps mb-1">Reasoning</div><p className="text-foreground/90 whitespace-pre-wrap">{active.reasoning}</p></div>}
              </div>

              {active.review && (
                <div className="mt-8 pt-6 border-t border-border" data-testid="j-review-block">
                  <div className="label-caps mb-3 flex items-center gap-1"><Sparkles className="h-3 w-3 text-primary" />Tribunal review</div>
                  {active.review.verdict && <div className="text-base font-medium mb-3">{active.review.verdict}</div>}
                  <div className="space-y-3 text-sm">
                    {active.review.calibration && <div><div className="label-caps mb-1">Calibration</div><div className="text-foreground/90">{active.review.calibration}</div></div>}
                    {active.review.framework_fit && <div><div className="label-caps mb-1">Framework fit</div><div className="text-foreground/90">{active.review.framework_fit}</div></div>}
                    {active.review.red_flags?.length > 0 && <div><div className="label-caps mb-1">Red flags</div><ul className="space-y-1">{active.review.red_flags.map((r, i) => <li key={i} className="flex gap-2 text-rose-400">· {r}</li>)}</ul></div>}
                    {active.review.questions_to_re_ask?.length > 0 && <div><div className="label-caps mb-1">Re-ask yourself</div><ul className="space-y-1">{active.review.questions_to_re_ask.map((q, i) => <li key={i} className="text-foreground/90">· {q}</li>)}</ul></div>}
                    {active.review.citations?.length > 0 && (
                      <div className="pt-3">
                        <div className="label-caps mb-2">Grounded in</div>
                        {active.review.citations.map((c) => (
                          <Link key={c.n} to={`/app/learn/chapter/${c.chapter_id}`} className="block text-xs font-mono text-muted-foreground hover:text-foreground">
                            [{c.n}] {c.title} <ArrowRight className="inline h-3 w-3 ml-1" />
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </Card>
          ) : (
            <Card className="p-10 text-center text-muted-foreground">Select an entry to view details, or log a new one.</Card>
          )}
        </div>
      </div>
    </div>
  );
}
